import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IUploadService } from "@shared/interfaces/upload-service.interface";
import { TierDto } from "@shared/types/tiers/tiers.dto";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
} from "antd";
import { useEffect } from "react";
import { useImageUpload } from "./useImageUpload";

const requiredFieldRule = {
  required: true,
  whitespace: false, // inputNumber doesn't support whitespace check properly usually, handled by type
  message: "This field is required",
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

interface IModal {
  open: boolean;
  tier: TierDto | null;
  onClose: () => void;
  onSave: (data: TierDto) => void;
  isLoading: boolean;
  isError?: boolean;
}

const ChangeTierModal = ({
  open,
  tier,
  onClose,
  onSave,
  isLoading,
  isError,
}: IModal) => {
  const isEditingMode = !!tier?.id;
  const [form] = Form.useForm();

  const iconUpload = useImageUpload();
  const lockedUpload = useImageUpload();

  const uploadService = appInject<IUploadService>(DI_TOKENS.uploadService);

  useEffect(() => {
    if (tier) {
      form.setFieldsValue({
        ...tier,
        icon: tier.icon ?? undefined,
        lockedIcon: tier.lockedIcon ?? undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isBonusApplicable: true,
        showGlobalRank: false,
        seasonEndTokenReward: 0,
      });
      iconUpload.reset();
      lockedUpload.reset();
    }
  }, [form, tier, open]);

  const handleFinish = (values: TierDto) => {
    const resolvedId = values.id ?? tier?.id;
    const hasId =
      typeof resolvedId === "string" && resolvedId.trim().length > 0;

    const { id, ...rest } = values;
    void id;
    const base = {
      ...rest,
      icon: values.icon || undefined,
      lockedIcon: values.lockedIcon || undefined,
      borderColor: values.borderColor?.trim() || undefined,
      bgColor: values.bgColor?.trim() || undefined,
      badgeBgColor: values.badgeBgColor?.trim() || undefined,
    };

    const payload = (hasId
      ? { ...base, id: resolvedId }
      : base) as TierDto;

    onSave(payload);
  };

  return (
    <Modal
      title={isEditingMode ? "Edit Tier" : "Add Tier"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item name="id" hidden>
          <Input type="hidden" />
        </Form.Item>

        {isError && (
          <Form.Item>
            <Alert
              message="Error"
              description="An error occurred while saving the tier data. Please try again."
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item name="name" label="Name" rules={[requiredFieldRule]}>
          <Input placeholder="e.g. Hall of Fame 🏆" />
        </Form.Item>

        {/* Image Upload Section */}
        <Form.Item label="Icon Image">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (file && file.size > MAX_FILE_SIZE_BYTES) {
                message.error(
                  `Image must be ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB or smaller`,
                );
                iconUpload.setPreview((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return null;
                });
                iconUpload.setFile(null);
                iconUpload.setUploaded(false);
                e.target.value = "";
                return;
              }
              iconUpload.setUploaded(false);
              iconUpload.setFile(file);
              iconUpload.setPreview((prev) => {
                if (prev) {
                  URL.revokeObjectURL(prev);
                }
                return file ? URL.createObjectURL(file) : null;
              });
            }}
          />
          {iconUpload.preview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={iconUpload.preview}
                alt="Selected"
                style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              />

              {iconUpload.uploaded === false ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    disabled={iconUpload.uploading}
                    onClick={async () => {
                      if (!iconUpload.file) return;
                      try {
                        iconUpload.setUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(iconUpload.file);
                        const urlStr =
                          typeof url === "string" && url.trim() !== ""
                            ? url
                            : "";
                        if (urlStr) {
                          iconUpload.setUploaded(true);
                          form.setFieldsValue({ icon: urlStr });
                        } else {
                          message.error("Upload returned no image URL");
                        }
                      } catch (err) {
                        message.error(
                          err instanceof Error
                            ? err.message
                            : "Failed to upload image",
                        );
                      } finally {
                        iconUpload.setUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {iconUpload.uploading ? "Uploading..." : "Use Image"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="icon"
          label="Icon URL (Optional)"
          rules={[{ max: 500, message: "Max 500 characters" }]}
        >
          <Input type="url" placeholder="https://..." maxLength={500} />
        </Form.Item>

        <Form.Item label="Locked icon image">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (file && file.size > MAX_FILE_SIZE_BYTES) {
                message.error(
                  `Image must be ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB or smaller`,
                );
                lockedUpload.setPreview((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return null;
                });
                lockedUpload.setFile(null);
                lockedUpload.setUploaded(false);
                e.target.value = "";
                return;
              }
              lockedUpload.setUploaded(false);
              lockedUpload.setFile(file);
              lockedUpload.setPreview((prev) => {
                if (prev) {
                  URL.revokeObjectURL(prev);
                }
                return file ? URL.createObjectURL(file) : null;
              });
            }}
          />
          {lockedUpload.preview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={lockedUpload.preview}
                alt="Locked preview"
                style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              />

              {lockedUpload.uploaded === false ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    disabled={lockedUpload.uploading}
                    onClick={async () => {
                      if (!lockedUpload.file) return;
                      try {
                        lockedUpload.setUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(lockedUpload.file);
                        const urlStr =
                          typeof url === "string" && url.trim() !== ""
                            ? url
                            : "";
                        if (urlStr) {
                          lockedUpload.setUploaded(true);
                          form.setFieldsValue({ lockedIcon: urlStr });
                        } else {
                          message.error("Upload returned no locked icon URL");
                        }
                      } catch (err) {
                        message.error(
                          err instanceof Error
                            ? err.message
                            : "Failed to upload locked icon image",
                        );
                      } finally {
                        lockedUpload.setUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {lockedUpload.uploading ? "Uploading..." : "Use image"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="lockedIcon"
          label="Locked icon URL (Optional)"
          rules={[{ max: 500, message: "Max 500 characters" }]}
          extra="Shown when the tier is locked; separate from the normal icon."
        >
          <Input type="url" placeholder="https://..." maxLength={500} />
        </Form.Item>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            name="rank"
            label="Tier Entry Threshold (GLO)"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="e.g. 1" />
          </Form.Item>

          <Form.Item
            name="gloThreshold"
            label="Tier Base GLO value"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="e.g. 10000" />
          </Form.Item>
        </div>

        <Form.Item
          name="percentile"
          label="Percentile (Visual)"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. Top 0.1%" />
        </Form.Item>

        <Form.Item name="isBonusApplicable" label="Is eligible for bonuses?">
          <Select placeholder="Select if eligible">
            <Select.Option value={true}>Yes</Select.Option>
            <Select.Option value={false}>No</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="showGlobalRank"
          label="Show global rank (users in this tier see rank/percentile)"
        >
          <Select placeholder="Select">
            <Select.Option value={true}>Yes</Select.Option>
            <Select.Option value={false}>No</Select.Option>
          </Select>
        </Form.Item>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 16,
          }}
        >
          <Form.Item
            name="borderColor"
            label="Border color (optional)"
            rules={[{ max: 50, message: "Max 50 characters" }]}
          >
            <Input placeholder="e.g. #3b82f6" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="bgColor"
            label="Background color (optional)"
            rules={[{ max: 50, message: "Max 50 characters" }]}
          >
            <Input placeholder="e.g. #eff6ff" maxLength={50} />
          </Form.Item>
        <Form.Item
          name="badgeBgColor"
          label="Badge background color (optional)"
          rules={[{ max: 50, message: "Max 50 characters" }]}
        >
          <Input placeholder="e.g. #2d2d44" maxLength={50} />
        </Form.Item>
        </div>

        <Form.Item
          name="seasonEndTokenReward"
          label="Season-end token reward"
          rules={[{ type: "number", min: 0, message: "Must be 0 or greater" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="e.g. 20"
          />
        </Form.Item>

        <Form.Item
          name="seasonEndPrizeDescription"
          label="Season-end prize description"
        >
          <Input.TextArea
            rows={2}
            placeholder="e.g. Season 0 badge + Nerfed"
          />
        </Form.Item>

        <Space
          style={{ justifyContent: "flex-end", width: "100%", marginTop: 15 }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditingMode ? "Save" : "Add"}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChangeTierModal;
